import { useEffect, useRef, useState } from "react";
import {
  AppAvatar,
  AppBox,
  AppButton,
  AppDateAndTime,
  AppGeneralSearch,
  AppIconButton,
  AppLink,
  AppText,
  AppTitle,
} from "../../../../commons/components";
import AppBorderBox from "../../../../commons/components/BorderBox";
import { useAppDispatch, useAppSelector } from "../../../../hooks/app";
import { ManageFootballTeam } from "../../../../commons/models/football/admin/manage-football-team";
import IconMultiselect from "../../../../commons/components/icons/multiselect";
import { TeamStatusEnum } from "../../../../commons/enums/team-status-enum";
import { FootballCompetitionTeam } from "../../../../commons/models/football/football-competition-team";
import { isLastIndex } from "../../../../commons/utilities/array-utils";
import { cleanUpGeneralSearchTitle, setGeneralCleanSearchField } from "../../../../redux/slices/general-search-slice";
import { toTitleCase } from "../../../../commons/utilities/string-utils";
import { StatusEnum } from "../../../../commons/enums/status-enum";
import {
  cleanUpSubTeamList,
  fetchManageSubTeamList,
} from "../../../../redux/slices/football/admin/team/sub-team/manage-sub-team-list-slice";
import { ManageFootballSubTeam } from "../../../../commons/models/football/admin/manage-football-sub-team";
import {
  cleanUpCompetitionSeasonAvailableTeams,
  fetchCompetitionSeasonAvailableTeams,
} from "../../../../redux/slices/football/admin/competition/manage-competition-season-available-teams-slice";
import AppLoader from "../../../../commons/components/Loader";
import {
  cleanUpCompetitionSeasonAvailableInviteTeams,
  fetchCompetitionSeasonAvailableInviteTeams,
} from "../../../../redux/slices/football/admin/competition/manage-competition-season-available-invite-teams-slice";
import { IFootballTeam } from "../../../../commons/models/football/interface/i-football-team";

/**
 * Provide competition and season to search the team of that competition only
 */

interface TeamSearchProps {
  onSelectedTeam?: (teams: ManageFootballTeam[]) => void;
  withoutMultiselect?: boolean;
  isTeamInvite?: boolean;
  isTeamInviteSelection?: boolean;
  competitionId?: number;
  seasonId?: number;
  userSelectedTeams?: ManageFootballTeam[];
  userSelectedCompetitionTeams?: FootballCompetitionTeam[];
  parentTeam?: IFootballTeam;
  withLoader?: boolean;
}
export default function TeamSearch({
  onSelectedTeam,
  withoutMultiselect,
  isTeamInvite,
  isTeamInviteSelection,
  competitionId,
  seasonId,
  userSelectedTeams,
  userSelectedCompetitionTeams,
  parentTeam,
  withLoader,
}: TeamSearchProps) {
  const dispatch = useAppDispatch();
  const multiSelectRef = useRef<HTMLInputElement>(null);
  const [isMultiSelect, setMultiSelect] = useState(false);
  const [isUncheckAll, setUncheckAll] = useState(false);
  const [selectedTeams, setSelectedTeams] = useState<ManageFootballTeam[]>([]);
  const [filteredTeams, setFilteredTeams] = useState<ManageFootballTeam[]>([]);
  const { competitionSeasonTeamAddResponseStatus } = useAppSelector(
    (state) => state.footballManageCompetitionSeasonTeamAdd
  );
  const { subTeamList } = useAppSelector((state) => state.footballManageSubTeamList);
  const { competitionSeasonAvailableTeams } = useAppSelector(
    (state) => state.footballManageCompetitionSeasonAvailableTeams
  );
  const { competitionSeasonAvailableInviteTeams } = useAppSelector(
    (state) => state.footballManageCompetitionSeasonAvailableInviteTeams
  );
  const { generalSearchTitle } = useAppSelector((state) => state.generalSearch);

  useEffect(() => {
    dispatch(setGeneralCleanSearchField(true));
    dispatch(cleanUpGeneralSearchTitle());
  }, []);

  useEffect(() => {
    handleDefaultTeamFilter();
  }, [
    subTeamList,
    competitionSeasonAvailableInviteTeams,
    competitionSeasonAvailableTeams,
    userSelectedTeams,
    userSelectedCompetitionTeams,
  ]);

  useEffect(() => {
    if (isUncheckAll) {
      setSelectedTeams([]);
      setUncheckAll(false);
    }
  }, [isUncheckAll]);

  const handleDefaultTeamFilter = () => {
    const selectedIds = new Set();
    if (userSelectedTeams && userSelectedTeams.length > 0) {
      userSelectedTeams.forEach((team) => selectedIds.add(team?.id));
    }
    if (userSelectedCompetitionTeams && userSelectedCompetitionTeams.length > 0) {
      userSelectedCompetitionTeams.forEach((competitionTeam) => selectedIds.add(competitionTeam.team?.id));
    }
    if (isTeamInvite) {
      if (competitionSeasonAvailableInviteTeams.length > 0) {
        let filteredTeams = [];
        if (selectedIds) {
          filteredTeams = competitionSeasonAvailableInviteTeams.all().filter((team) => !selectedIds.has(team.id));
        } else {
          filteredTeams = competitionSeasonAvailableInviteTeams.all();
        }
        setFilteredTeams(filteredTeams);
      } else {
        setFilteredTeams([]);
      }
    } else if (isTeamInviteSelection) {
      if (subTeamList.length >= 0) {
        let filteredTeams = [];
        const teamlist = [new ManageFootballSubTeam(parentTeam), ...subTeamList.all()];
        if (selectedIds) {
          filteredTeams = teamlist.filter((team) => !selectedIds.has(team.id));
        } else {
          filteredTeams = teamlist;
        }
        setFilteredTeams(filteredTeams as unknown as ManageFootballTeam[]);
      } else {
        setFilteredTeams([]);
      }
    } else {
      if (competitionSeasonAvailableTeams.length > 0) {
        let filteredTeams = [];
        if (selectedIds) {
          filteredTeams = competitionSeasonAvailableTeams.all().filter((team) => !selectedIds.has(team.id));
        } else {
          filteredTeams = competitionSeasonAvailableTeams.all();
        }
        setFilteredTeams(filteredTeams);
      } else {
        setFilteredTeams([]);
      }
    }
  };

  const handleTeamClick = (team: ManageFootballTeam) => {
    if (onSelectedTeam) {
      onSelectedTeam([team]);
    }
  };

  const handleMultiSelectClick = (e: any) => {
    e.preventDefault();
    multiSelectRef.current?.click();
  };

  const handleTeamSelection = (e: any, team: ManageFootballTeam) => {
    if (e.target.checked) {
      setSelectedTeams([...selectedTeams, team]);
    } else {
      setSelectedTeams(selectedTeams.filter((t) => t.id !== team.id));
    }
  };

  const handleSaveTeams = () => {
    if (onSelectedTeam) {
      onSelectedTeam(selectedTeams);
    }
  };

  useEffect(() => {
    const filterParams = {
      sort: "firstName",
      name: generalSearchTitle,
      status: TeamStatusEnum.Active,
      page: 0,
    };
    if (isTeamInvite) {
      dispatch(cleanUpCompetitionSeasonAvailableInviteTeams());
      dispatch(
        fetchCompetitionSeasonAvailableInviteTeams({
          competitionId: competitionId!,
          seasonId: seasonId!,
          urlParams: {
            ...filterParams,
            hostTeamId: parentTeam?.id,
          },
        })
      );
    } else if (isTeamInviteSelection) {
      dispatch(cleanUpSubTeamList());
      dispatch(fetchManageSubTeamList({ teamId: parentTeam?.id!, urlParams: filterParams }));
    } else {
      dispatch(cleanUpCompetitionSeasonAvailableTeams());
      dispatch(
        fetchCompetitionSeasonAvailableTeams({
          competitionId: competitionId!,
          seasonId: seasonId!,
          urlParams: {
            ...filterParams,
            hostTeamId: parentTeam?.id,
          },
        })
      );
    }
  }, [generalSearchTitle]);

  return (
    <>
      {withLoader && <AppLoader size="md" />}
      <AppBox flexDirection="column" gap="md">
        <AppBox flexDirection="column" gap="sm">
          <AppBox gap="xs">
            <AppBox className="w-100 flex-1">
              <AppGeneralSearch />
            </AppBox>
            {withoutMultiselect ?? (
              <>
                <AppIconButton
                  icon={<IconMultiselect />}
                  variant="light"
                  onClick={(e) => handleMultiSelectClick(e)}
                  color={isMultiSelect ? "primary" : "gray"}
                  borderLight
                />
                <input
                  type="checkbox"
                  hidden
                  ref={multiSelectRef}
                  checked={isMultiSelect}
                  onChange={(e) => {
                    setMultiSelect(e.target.checked);
                    setSelectedTeams([]);
                  }}
                />
              </>
            )}
          </AppBox>
          <AppBox flexDirection="column" style={{ height: "25rem", overflowY: "scroll" }}>
            {filteredTeams &&
              filteredTeams.map((team, index) => (
                <AppBorderBox border={isLastIndex(index, filteredTeams) ? [] : ["Bottom"]} py="xs" key={index}>
                  {isMultiSelect ? (
                    <AppBox justifyContent="space-between" alignItems="center" pr="xs" className="flex-1">
                      <AppBox gap="xs" alignItems="center">
                        <AppAvatar username={team.name} src={team.logo} size="md" />
                        <AppBox flexDirection="column">
                          <AppTitle as="h6">{toTitleCase(team.shortName)}</AppTitle>
                          {team.founded && (
                            <AppBox gap="3xs" alignItems="center">
                              <AppText as="span" fontWeight="semibold" size="sm" color="muted">
                                Foundation Date:
                              </AppText>
                              <AppDateAndTime date={team.founded} format="DD MMM, YYYY" size="sm" color="muted" />
                            </AppBox>
                          )}
                        </AppBox>
                      </AppBox>
                      <label className="ee-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedTeams.some((t) => t.id === team.id)}
                          onChange={(e) => {
                            handleTeamSelection(e, team);
                          }}
                        />
                        <span className="checkmark"></span>
                      </label>
                    </AppBox>
                  ) : (
                    <AppLink onClick={() => handleTeamClick(team)}>
                      <AppBox justifyContent="space-between" alignItems="center" className="flex-1">
                        <AppBox gap="xs" alignItems="center">
                          <AppAvatar username={team.name} src={team.logo} size="sm" />
                          <AppBox flexDirection="column">
                            <AppTitle as="h6">{toTitleCase(team.shortName)}</AppTitle>
                            {team.founded && (
                              <AppBox gap="3xs" alignItems="center">
                                <AppText as="span" fontWeight="semibold" size="sm" color="muted">
                                  Foundation Date:
                                </AppText>
                                <AppDateAndTime date={team.founded} format="DD MMM, YYYY" size="sm" color="muted" />
                              </AppBox>
                            )}
                          </AppBox>
                        </AppBox>
                      </AppBox>
                    </AppLink>
                  )}
                </AppBorderBox>
              ))}
            {filteredTeams.length === 0 && <AppText>There are not any team</AppText>}
          </AppBox>
        </AppBox>
        {isMultiSelect && (
          <AppBox justifyContent="end" gap="xs">
            {selectedTeams.length > 0 && (
              <AppButton
                variant="outline"
                borderLight
                label="Clear"
                color="gray"
                onClick={() => setUncheckAll(true)}
                disabled={competitionSeasonTeamAddResponseStatus === StatusEnum.Loading}
              />
            )}
            <AppButton
              label={isTeamInvite ? "Invite Teams" : "Add Teams"}
              onClick={handleSaveTeams}
              disabled={filteredTeams.length === 0 || selectedTeams.length === 0}
              loading={competitionSeasonTeamAddResponseStatus === StatusEnum.Loading}
            />
          </AppBox>
        )}
      </AppBox>
    </>
  );
}
